/**
 * คณิตการวางโมเดล .glb ให้พอดีกับตัวรับคลิกของเกม (pure function มีเทสต์)
 *
 * ไฟล์จากเครื่องมือ image-to-3D แต่ละตัวมาคนละขนาดและจุดกำเนิดไม่แน่นอน
 * จึงปรับเองทุกครั้ง: ด้านที่ยาวที่สุดของกล่องรอบตัว = `size × SIZE_FILL`
 * กึ่งกลางแนวนอนอยู่ที่ 0 และเท้า (ก้นกล่อง) อยู่ที่ y = 0
 * ใช้ด้านที่ยาวที่สุด ไม่ใช่ความสูง ไม่งั้นตัวแบน ๆ อย่างปูหรือหนอนทรายจะกว้างเกินจอ
 */

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/** ปรับรายตัวหลังลองดูในเกม */
export interface GlbTweak {
  /** หมุนเพิ่ม (เรเดียน) ถ้าโมเดลหันผิดทาง — เกมถือว่าหน้าตัวละครหันไป +Z */
  yaw?: number;
  /** คูณขนาดเพิ่มจากที่คิดให้อัตโนมัติ */
  scale?: number;
  /** ยกขึ้น/กดลง (หน่วยโลก) */
  lift?: number;
}

export interface Fit {
  scale: number;
  yaw: number;
  x: number;
  y: number;
  z: number;
}

/** รูปทรงเดิมเป็นทรงกลมเส้นผ่าน `size` โมเดลจริงมีส่วนยื่น (หู หาง อาวุธ) จึงให้ใหญ่กว่านิดหน่อย */
export const SIZE_FILL = 1.2;

export function fitTransform(min: Vec3Like, max: Vec3Like, size: number, tweak: GlbTweak = {}): Fit {
  const dx = max.x - min.x;
  const dy = max.y - min.y;
  const dz = max.z - min.z;
  const longest = Math.max(dx, dy, dz);
  const scale = longest > 0 ? ((size * SIZE_FILL) / longest) * (tweak.scale ?? 1) : 1;
  const yaw = tweak.yaw ?? 0;
  const cx = (min.x + max.x) / 2;
  const cz = (min.z + max.z) / 2;
  // ลำดับของ Babylon: ย่อ/ขยาย → หมุน → เลื่อน จึงต้องหมุนจุดกึ่งกลางก่อนแล้วค่อยหักออก
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  const rx = cx * cos + cz * sin;
  const rz = -cx * sin + cz * cos;
  return {
    scale,
    yaw,
    x: -rx * scale,
    y: -min.y * scale + (tweak.lift ?? 0),
    z: -rz * scale,
  };
}
